import type { MutableRefObject } from "react";

import type {
  TableCallAudioIssueKind,
  TableCallAudioReportDirection,
} from "./table-call-audio-reporting.js";
import type {
  MeetingClient,
  TableCallAudioReadiness,
  TableCallStatus,
} from "./table-call.context.js";

export interface TableCallTarget {
  key: string;
  tableId: string;
  fluidId: string;
}

export interface AudioRecoveryCounters {
  microphoneSameDeviceAttempts: number;
  microphoneFallbackAttempts: number;
  speakerFallbackAttempts: number;
}

export interface DeviceSelectionTransactionArgs {
  kind: "audioinput" | "audiooutput";
  deviceId: string;
}

export interface AudioDiagnosticClassification {
  direction: TableCallAudioReportDirection;
  operation: string;
  issueKind: TableCallAudioIssueKind;
  recoverable: boolean;
  deviceKind?: "audioinput" | "audiooutput" | "none";
  preferredDeviceId?: string | null;
  currentDeviceId?: string | null;
  fallbackDeviceId?: string | null;
  rtkPermissionMessage?: string | null;
  recoveryAttempt?: number;
}

export type EnqueueDeviceApplyResult =
  | {
      status: "applied";
      appliedMicrophoneId: string | null;
      appliedSpeakerId: string | null;
    }
  | { status: "skipped" }
  | { status: "failed" };

export type SerializedQueueRef = MutableRefObject<Promise<void>>;

export interface TableCallRuntime {
  meetingRef: MutableRefObject<MeetingClient | null>;
  desiredTargetRef: MutableRefObject<TableCallTarget | null>;
  activeTargetRef: MutableRefObject<TableCallTarget | null>;
  operationGenerationRef: MutableRefObject<number>;
  isMountedRef: MutableRefObject<boolean>;
  lifecycleQueueRef: SerializedQueueRef;
  deviceApplyQueueRef: SerializedQueueRef;
  microphoneToggleQueueRef: SerializedQueueRef;
  isReconnectingRef: MutableRefObject<boolean>;
  reconnectRequestIdRef: MutableRefObject<number>;
  reconnectReasonRef: MutableRefObject<string | null>;
  scheduleTransitionRef: MutableRefObject<() => void>;
  statusRef: MutableRefObject<TableCallStatus>;
  audioReadinessRef: MutableRefObject<TableCallAudioReadiness>;
  lastRemoteFailureReportKeyRef: MutableRefObject<string | null>;
  audioRecoveryCountersRef: MutableRefObject<AudioRecoveryCounters>;
}

export interface CurrentOperationArgs {
  target: TableCallTarget;
  targetMeeting: MeetingClient;
  generation: number;
}

export type AudioStateSetter = (
  readiness: TableCallAudioReadiness,
  message?: string | null
) => void;

export interface TableCallDiagnostics {
  classify: (args: {
    error: unknown;
    direction: TableCallAudioReportDirection;
    operation: string;
    deviceKind?: "audioinput" | "audiooutput" | "none";
    preferredDeviceId?: string | null;
    currentDeviceId?: string | null;
    fallbackDeviceId?: string | null;
    rtkPermissionMessage?: string | null;
    meeting?: MeetingClient | null;
    target?: TableCallTarget | null;
    recoveryAttempt?: number;
  }) => AudioDiagnosticClassification;
  report: (
    error: unknown,
    args: {
      event: string;
      classification: AudioDiagnosticClassification;
      extra?: Record<string, unknown>;
    }
  ) => void;
  reportDeviceSyncRaceDropped: (reason: string) => void;
}

export const AUDIO_PERMISSION_DENIED_MESSAGES = new Set([
  "DENIED",
  "SYSTEM_DENIED",
  "NO_DEVICES_AVAILABLE",
]);

export const MICROPHONE_UNAVAILABLE_MESSAGE =
  "The selected microphone could not start.";
export const OUTPUT_FALLBACK_MESSAGE =
  "The selected speaker could not be used. Audio is playing through the system default output.";
export const INPUT_PERMISSION_MESSAGE =
  "Microphone access is blocked. Allow microphone access in your browser or system settings.";
